import React, { useState } from 'react';
import { Plane, Search, AlertCircle, Calendar, Hash, MapPin, Clock, Navigation, Gauge, Route as RouteIcon, Package, ArrowRight } from 'lucide-react';

// Replace the formatTime function with this:
const formatTime = (timestamp) => {
  if (!timestamp) return '--:--';
  // Parse the ISO string and extract the local time components
  // Format: 2025-11-23T15:45:00-06:00
  const match = timestamp.match(/T(\d{2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1]);
    const minutes = match[2];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  }
  // Fallback to Date parsing
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

// Replace the formatDate function with this:
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  // Extract date portion: 2025-11-23T15:45:00-06:00
  const match = timestamp.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const date = new Date(match[1], parseInt(match[2]) - 1, match[3]);
    return date.toLocaleDateString('en-US', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
  }
  // Fallback
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
};

const parseFlightIdentifier = (flightId) => {
  if(!flightId) return null;
  // Format: AAL51-1763795475-airline-1135p
  // Extract airline code and flight number from the first part
  const parts = flightId.split('-');
  const flightPart = parts[0]; // "AAL51"
  
  // Extract airline code (first 2-3 letters) and flight number
  const airlineMatch = flightPart.match(/^([A-Z]{2,3})(\d+)$/);
  
  if (airlineMatch) {
    const airlineCode = airlineMatch[1]; // "AAL"
    const flightNumber = airlineMatch[2]; // "51"
    
    // Convert 3-letter ICAO code to 2-letter IATA if needed
    // AAL -> AA, DAL -> DL, UAL -> UA, etc.
    const iataCode = airlineCode.length === 3 ? airlineCode.slice(0, 2) : airlineCode;
    
    return {
      airlineCode: iataCode,
      flightNumber: flightNumber,
      displayName: `${iataCode} Flight ${flightNumber}`
    };
  }
  
  return null;
};

const formatDuration = (seconds) => {
  if (!seconds) return 'N/A';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const getStatusInfo = (status) => {
  if (!status) return { text: 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
  const statusLower = status.toLowerCase();
  if (statusLower.includes('arrived') || statusLower.includes('gate arrival')) 
    return { text: 'Arrived', color: '#059669', bg: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' };
  if (statusLower.includes('en route')) 
    return { text: 'En Route', color: '#f59e0b', bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' };
  if (statusLower.includes('departed')) 
    return { text: 'Departed', color: '#3b82f6', bg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' };
  if (statusLower.includes('delay')) 
    return { text: 'Delayed', color: '#ef4444', bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' };
  if (statusLower.includes('cancel')) 
    return { text: 'Cancelled', color: '#dc2626', bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' };
  return { text: status, color: '#6b7280', bg: '#f3f4f6' };
};

// Search Form Component - Bootstrap Responsive
const SearchForm = ({ airlineCode, setAirlineCode, flightNumber, setFlightNumber, flightDate, setFlightDate, handleSearch, loading, minDateString, maxDateString }) => (
  <div className="container" style={{ maxWidth: '1200px', margin: '20px auto' }}>
    <div style={{ 
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
      padding: '16px',
    }}>
      <div className="row g-2">
        <div className="col-12 col-md-2">
          <div style={{ position: 'relative' }}>
            <Hash style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#9ca3af', zIndex: 1}}/>
            <input
              type="text"
              className="form-control"
              value={airlineCode}
              onChange={e => setAirlineCode(e.target.value.toUpperCase())}
              placeholder="Airline Code"
              maxLength="3"
              required 
              style={{
                paddingLeft: '38px',
                fontSize: '14px',
                borderRadius: '10px',
                border: '1.5px solid #e5e7eb',
                fontWeight: '600',
                color: '#374151',
                height: '42px'
                 
              }}
              onFocus={e => {
                e.target.style.borderColor='#667eea';
                e.target.style.boxShadow='0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={e => {
                e.target.style.borderColor='#e5e7eb';
                e.target.style.boxShadow='none';
              }}
            />
          </div>
        </div>

        <div className="col-12 col-md-3">
          <div style={{ position: 'relative' }}>
            <Plane style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#9ca3af', zIndex: 1 }} />
            <input
              type="text"
              className="form-control"
              value={flightNumber}
              onChange={e => setFlightNumber(e.target.value)}
              placeholder="Flight #"
              required
              style={{
                paddingLeft: '38px',
                fontSize: '14px',
                borderRadius: '10px',
                border: '1.5px solid #e5e7eb',
                fontWeight: '600',
                color: '#374151',
                height: '42px'
              }}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              onFocus={e => {
                e.target.style.borderColor='#667eea';
                e.target.style.boxShadow='0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={e => {
                e.target.style.borderColor='#e5e7eb';
                e.target.style.boxShadow='none';
              }}
            />
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div style={{ position: 'relative' }}>
            <Calendar style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '18px', height: '18px', color: '#9ca3af', zIndex: 1 }} />
            
            <input
              type="date"
              className="form-control"
              value={flightDate}
              onChange={e => setFlightDate(e.target.value)}
              min={minDateString}
              max={maxDateString}
              required
              style={{
                paddingLeft: '38px',
                fontSize: '14px',
                borderRadius: '10px',
                border: '1.5px solid #e5e7eb',
                fontWeight: '600',
                color: '#374151',
                height: '42px'
              }}
              onFocus={e => {
                e.target.style.borderColor='#667eea';
                e.target.style.boxShadow='0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={e => {
                e.target.style.borderColor='#e5e7eb';
                e.target.style.boxShadow='none';
              }}
            />
          </div>
        </div>

        <div className="col-12 col-md-3">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="btn w-100"
            style={{
              borderRadius: '10px',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: '700',
              fontSize: '14px',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              height: '42px',
              padding: '0 16px'
            }}
            onMouseEnter={e => !loading && (e.target.style.transform = 'translateY(-2px)', e.target.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)')}
            onMouseLeave={e => !loading && (e.target.style.transform = 'translateY(0)', e.target.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)')}
          >
            {loading ? (
              <div style={{ 
                width: '20px', 
                height: '20px', 
                border: '3px solid #ffffff40', 
                borderTopColor: '#ffffff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <Search style={{ width: '18px', height: '18px' }} />
            )}
            <span className="d-none d-md-inline">Search</span>
          </button>
        </div>
      </div>
    </div>

    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Status Banner Component - Mobile Responsive
const StatusBanner = ({ flightData, statusInfo }) => (
  <div style={{ 
    background: statusInfo.bg,
    borderRadius: '16px', 
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1', minWidth: '200px' }}>
        <img 
          src={`https://images.kiwi.com/airlines/64/${flightData.operator_iata}.png`}
          alt="Airline" 
          style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'white', padding: '4px', flexShrink: 0 }} 
          onError={(e) => {e.target.style.display = 'none'}} 
        />
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 'clamp(20px, 5vw, 32px)', fontWeight: 'bold', color: statusInfo.color, margin: 0, wordBreak: 'break-word' }}>
            {flightData.operator_iata || flightData.operator} {flightData.flight_number}
          </h2>
          <div style={{ fontSize: '12px', color: '#374151', marginTop: '4px', fontWeight: '500' }}>
            {flightData.ident_iata} • {flightData.aircraft_type}
          </div>
        </div>
      </div>
      <div style={{ 
        background: 'rgba(255,255,255,0.9)',
        padding: '10px 20px', 
        borderRadius: '12px', 
        fontSize: '16px', 
        fontWeight: '700',
        color: statusInfo.color,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        flexShrink: 0
      }}>
        {statusInfo.text}
      </div>
    </div>
  </div>
);

// Airport Info Component - Mobile Responsive
const AirportInfo = ({ airport, time, isOrigin, flightData }) => {
  const timeKey = isOrigin ? 'out' : 'in';
  const actualTime = flightData[`actual_${timeKey}`];
  const estimatedTime = flightData[`estimated_${timeKey}`];
  const scheduledTime = flightData[`scheduled_${timeKey}`];
  const displayTime = actualTime || estimatedTime || scheduledTime;
  
  const bgGradient = isOrigin 
    ? 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)'
    : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
  
  const textColor = isOrigin ? '#312e81' : '#064e3b';
  const accentColor = isOrigin ? '#4f46e5' : '#065f46';

  // Get runway info
  const runway = isOrigin ? flightData.actual_runway_off : flightData.actual_runway_on;
  const gate = isOrigin ? flightData.gate_origin : flightData.gate_destination;
  const terminal = isOrigin ? flightData.terminal_origin : flightData.terminal_destination;

  return (
    <div style={{ textAlign: isOrigin ? 'left' : 'right' }}>
      <div style={{ fontSize: 'clamp(36px, 10vw, 56px)', fontWeight: 'bold', color: '#111827', marginBottom: '8px', lineHeight: '1' }}>
        {airport.code_iata || airport.code}
      </div>
      <div style={{ fontSize: 'clamp(16px, 4vw, 20px)', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
        {airport.city}
      </div>
      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
        {airport.name}
      </div>
      
      <div style={{ background: bgGradient, padding: '16px', borderRadius: '12px' }}>
        <div style={{ fontSize: '11px', color: accentColor, fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {formatDate(scheduledTime)}
        </div>
        <div style={{ fontSize: 'clamp(24px, 6vw, 36px)', fontWeight: 'bold', color: textColor, marginBottom: '6px' }}>
          {formatTime(displayTime)}
        </div>
        <div style={{ fontSize: '13px', color: accentColor, fontWeight: '600', marginBottom: '12px' }}>
          {actualTime ? `✓ ${isOrigin ? 'Actual Departure' : 'Arrived'}` : estimatedTime ? '~ Estimated' : '○ Scheduled'}
          {!isOrigin && flightData.arrival_delay > 0 && (
            <span style={{ color: '#dc2626', marginLeft: '8px', display: 'block', marginTop: '4px' }}>
              (+{Math.floor(flightData.arrival_delay / 60)} min delay)
            </span>
          )}
        </div>
        
        {(gate || terminal || runway) && (
          <div style={{ paddingTop: '12px', borderTop: '2px solid rgba(255,255,255,0.5)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(gate || terminal) && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: isOrigin ? 'flex-start' : 'flex-end', gap: '6px', fontSize: '12px', color: textColor, fontWeight: '600', flexWrap: 'wrap' }}>
                {isOrigin && <MapPin style={{ width: '16px', height: '16px' }} />}
                <span>
                  {terminal && `Terminal ${terminal}`}
                  {terminal && gate && ' • '}
                  {gate && `Gate ${gate}`}
                </span>
                {!isOrigin && <MapPin style={{ width: '16px', height: '16px' }} />}
              </div>
            )}
            
            {runway && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: isOrigin ? 'flex-start' : 'flex-end', gap: '6px', fontSize: '12px', color: textColor, fontWeight: '600', flexWrap: 'wrap' }}>
                {isOrigin && <Navigation style={{ width: '16px', height: '16px' }} />}
                <span>{isOrigin ? 'Departure' : 'Arrival'} Runway {runway}</span>
                {!isOrigin && <Navigation style={{ width: '16px', height: '16px' }} />}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Flight Progress Component - Mobile Responsive
const FlightProgress = ({ progressPercent, flightData }) => (
  <div style={{ textAlign: 'center', width: '90%',  margin: '0 auto' }}>
    <div style={{ position: 'relative', marginBottom: '20px' }}>
      <div style={{ height: '6px', background: 'linear-gradient(to right, #e5e7eb, #d1d5db)', borderRadius: '3px', width: '100%', position: 'relative', overflow: 'hidden' }}>
        <div style={{ 
          height: '100%', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '3px',
          width: `${progressPercent}%`,
          transition: 'width 1s ease-out',
          boxShadow: '0 0 10px rgba(102, 126, 234, 0.5)'
        }}></div>
      </div>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: `${progressPercent}%`,
        transform: 'translate(-50%, -50%)',
        background: 'white',
        borderRadius: '50%',
        padding: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <Plane style={{ width: '24px', height: '24px', color: '#667eea', transform: 'rotate(90deg)' }} />
      </div>
    </div>
    <div style={{ marginTop: '24px' }}>
      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
        Flight Duration
      </div>
      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
        {formatDuration(flightData.filed_ete)}
      </div>
      <div style={{ fontSize: '14px', color: '#667eea', fontWeight: '600' }}>
        {progressPercent}% Complete
      </div>
      {flightData.route_distance && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
          {flightData.route_distance} nm
        </div>
      )}
    </div>
  </div>
);

// Info Card Component
const InfoCard = ({ title, icon: Icon, gradient, items }) => (
  <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
      <div style={{ background: gradient, padding: '10px', borderRadius: '10px' }}>
        <Icon style={{ width: '20px', height: '20px', color: 'white' }} />
      </div>
      <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827', margin: 0 }}>{title}</h3>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#6b7280', fontSize: '13px' }}>{item.label}</span>
          <span style={{ fontWeight: '600', color: item.color || '#111827', fontSize: item.fontSize || '14px', textAlign: 'right' }}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  </div>
);

// Timing Table Component - Mobile Responsive
const TimingTable = ({ flightData }) => (
  <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
      Detailed Timing
    </h3>
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
        <thead>
          <tr style={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
            <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Event</th>
            <th style={{ padding: '10px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Scheduled</th>
            <th style={{ padding: '10px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Estimated</th>
            <th style={{ padding: '10px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#374151' }}>Actual</th>
          </tr>
        </thead>
        <tbody>
          {[
            { label: 'Gate Departure', key: 'out' },
            { label: 'Runway Takeoff', key: 'off' },
            { label: 'Runway Landing', key: 'on' },
            { label: 'Gate Arrival', key: 'in' }
          ].map((row, idx) => (
            <tr key={idx} style={{ borderBottom: idx < 3 ? '1px solid #e5e7eb' : 'none' }}>
              <td style={{ padding: '10px', fontSize: '13px', color: '#6b7280' }}>{row.label}</td>
              <td style={{ padding: '10px', textAlign: 'center', fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                {formatTime(flightData[`scheduled_${row.key}`])}
              </td>
              <td style={{ padding: '10px', textAlign: 'center', fontSize: '13px', color: '#111827', fontWeight: '500' }}>
                {formatTime(flightData[`estimated_${row.key}`])}
              </td>
              <td style={{ padding: '10px', textAlign: 'center', fontSize: '13px', color: '#059669', fontWeight: '700' }}>
                {formatTime(flightData[`actual_${row.key}`])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Main Component
const FlightStatusComponent = () => {
  const [flightData, setFlightData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [airlineCode, setAirlineCode] = useState();
  const [flightNumber, setFlightNumber] = useState();
  
  // Set default date to today and calculate min/max dates
  const today = new Date();
  const minDate = new Date(today);
  minDate.setDate(today.getDate() - 7);
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 3);

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const minDateString = formatDate(minDate);
  const maxDateString = formatDate(maxDate);
  
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  const [flightDate, setFlightDate] = useState(formatDateForInput(today));

  const handleSearch = async () => {
    if (!airlineCode || !flightNumber || !flightDate) {
      setError('Please fill in all fields');
      setFlightData(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const flightIdent = `${airlineCode}${flightNumber}`.toLowerCase();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/flight/${flightIdent}?date=${flightDate}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'x-apikey': 'X67IGBNvK33nR1dBWRbk1DUbo7ZmKJOh'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const data = await response.json();

      // Backend may send: { "message": "No departing or in-flight segment found" }
      if (data.message) {
        setError(data.message);
        setFlightData(null);
        return;
      }

      // Directly set the returned flight object
      setFlightData(data);

    } catch (err) {
      setError(err.message);
      setFlightData(null);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f0f4f8, #d9e2ec)', padding: '16px' }}>
      {/* Search Form always on top */}
      <SearchForm 
        airlineCode={airlineCode}
        setAirlineCode={setAirlineCode}
        flightNumber={flightNumber}
        setFlightNumber={setFlightNumber}
        flightDate={flightDate}
        setFlightDate={setFlightDate}
        handleSearch={handleSearch}
        loading={loading}
        minDateString={minDateString}
        maxDateString={maxDateString}
      />

      {/* Display flight info or loading/error below */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 8px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTopColor: '#667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}
            />
            <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading flight information...</p>
          </div>
        )}

        {error && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px 20px', textAlign: 'center', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
            <AlertCircle style={{ width: '48px', height: '48px', color: '#ef4444', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>Flight Not Found</h2>
            <button 
              onClick={() => { setError(null); setFlightData(null); }}
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white', 
                padding: '12px 32px', 
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              Search Again
            </button>
          </div>
        )}

        {flightData && !loading && !error && (
          <>
            <StatusBanner flightData={flightData} statusInfo={getStatusInfo(flightData.status)} />

            <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden', marginBottom: '20px' }}>
              <div style={{ padding: '24px 16px' }}>
                <div className="row align-items-center g-4">
                  <div className="col-12 col-md-3">
                  <AirportInfo airport={flightData.origin || {}} time={flightData.scheduled_out} isOrigin={true} flightData={flightData} />
                  </div>
                  <div className="col-12 col-md-6"><FlightProgress progressPercent={flightData.progress_percent || 0} flightData={flightData} />
                  </div>  <div className="col-12 col-md-3"><AirportInfo airport={flightData.destination || {}} time={flightData.scheduled_in} isOrigin={false} flightData={flightData} />
                </div></div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <InfoCard
                title="Aircraft Details"
                icon={Plane}
                gradient="linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)"
                items={[
                  { label: 'Aircraft Type', value: flightData.aircraft_type },
                  { label: 'Registration', value: flightData.registration },
                  { label: 'Inbound Flight', value: parseFlightIdentifier(flightData.inbound_fa_flight_id)?.displayName || '', fontSize: '12px' }
                ]}
              />

              <InfoCard
                title="Flight Performance"
                icon={Gauge}
                gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                items={[
                  { label: 'Filed Airspeed', value: `${flightData.filed_airspeed} kts` },
                  { label: 'Filed Altitude', value: flightData.filed_altitude ? `FL${flightData.filed_altitude}` : '' },
                  { label: 'Distance', value: `${flightData.route_distance} nm` }
                ]}
              />

              <InfoCard
                title="Timing Details"
                icon={Clock}
                gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                items={[
                  { 
                    label: 'Departure Delay', 
                    value: flightData.departure_delay > 0 ? `+${Math.floor(flightData.departure_delay / 60)} min` : 'On Time',
                    color: flightData.departure_delay > 0 ? '#ef4444' : '#10b981'
                  },
                  { 
                    label: 'Arrival Delay', 
                    value: flightData.arrival_delay > 0 ? `+${Math.floor(flightData.arrival_delay / 60)} min` : 'On Time',
                    color: flightData.arrival_delay > 0 ? '#ef4444' : '#10b981'
                  },
                  { label: 'Flight Duration', value: formatDuration(flightData.filed_ete) }
                ]}
              />
            </div>

            {flightData.codeshares_iata && flightData.codeshares_iata.length > 0 && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', padding: '10px', borderRadius: '10px' }}>
                    <ArrowRight style={{ width: '20px', height: '20px', color: 'white' }} />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                    Also Marketed As
                  </h3>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {flightData.codeshares_iata.map((code, idx) => (
                    <div key={idx} style={{ 
                      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                      color: '#075985', 
                      padding: '8px 16px', 
                      borderRadius: '10px', 
                      fontSize: '14px', 
                      fontWeight: '700',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      {code}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {flightData.route && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', padding: '10px', borderRadius: '10px' }}>
                    <RouteIcon style={{ width: '20px', height: '20px', color: 'white' }} />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827', margin: 0 }}>Flight Route</h3>
                </div>
                <div style={{ 
                  background: '#f9fafb', 
                  padding: '14px', 
                  borderRadius: '10px',
                  fontSize: '12px',
                  color: '#374151',
                  lineHeight: '1.8',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  overflowX: 'auto'
                }}>
                  {flightData.route}
                </div>
              </div>
            )}

            <TimingTable flightData={flightData} />

            {/* {flightData.inbound_fa_flight_id && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>Inbound Flight ID:</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {flightData.inbound_fa_flight_id}
                  </span>
                </div>
              </div>
            )} */}
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (min-width: 768px) {
          .flight-layout {
            grid-template-columns: 1fr auto 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FlightStatusComponent;